// Serializes staged lote features back into a KML the backend partial-import parser accepts:
// it requires a top-level <Folder> named one of paddocks|lotes|potreros|cuadros|loteo and reads
// each paddock name from its Placemark's <name>. The repo's tokml dependency emits flat
// placemarks with no folder wrapper, so this is hand-rolled. Multi-piece geometries (a client
// clip can split a lote) are emitted as <MultiGeometry> per the ID 5441 upload contract.
const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const serializeRing = (ring: number[][]): string =>
  ring.map(([longitude, latitude]) => `${longitude},${latitude}`).join(' ')

const serializeBoundary = (ring: number[][], boundaryTag: string): string =>
  `<${boundaryTag}><LinearRing><coordinates>${serializeRing(ring)}</coordinates></LinearRing></${boundaryTag}>`

const serializePolygon = (rings: number[][][]): string => {
  if (!rings.length) return ''
  const [outerRing, ...innerRings] = rings
  const boundaries = [
    serializeBoundary(outerRing, 'outerBoundaryIs'),
    ...innerRings.map(ring => serializeBoundary(ring, 'innerBoundaryIs')),
  ]
  return `<Polygon>${boundaries.join('')}</Polygon>`
}

const serializeGeometry = (geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): string => {
  if (geometry.type === 'MultiPolygon') {
    const polygons = geometry.coordinates.map(serializePolygon)
    return `<MultiGeometry>${polygons.join('')}</MultiGeometry>`
  }
  return serializePolygon(geometry.coordinates)
}

export const buildPaddocksKml = (
  features: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>[],
): string => {
  const placemarks = features
    .map(
      feature =>
        `<Placemark><name>${escapeXml(String(feature.properties?.name ?? ''))}</name>${serializeGeometry(feature.geometry)}</Placemark>`,
    )
    .join('')

  return `<?xml version="1.0" encoding="UTF-8"?><kml xmlns="http://www.opengis.net/kml/2.2"><Document><Folder><name>lotes</name>${placemarks}</Folder></Document></kml>`
}
